/**
 * @module TabComponent
 * Manages an accessible tab component, handling clicks, keyboard navigation,
 * and dynamic panel height adjustments.
 */
class TabComponent {
    /**
     * @param {HTMLElement} element - The main container element for the tab component.
     */
    constructor(element) {
        this.container = element;
        this.tablist = this.container.querySelector('[role="tablist"]');
        this.tabs = Array.from(this.container.querySelectorAll('[role="tab"]'));
        this.panels = Array.from(this.container.querySelectorAll('[role="tabpanel"]'));
        this.panelsContainer = this.container.querySelector('.tab-layout__panels');

        if (!this.tablist || this.tabs.length === 0 || this.panels.length === 0 || !this.panelsContainer) {
            console.error('Tab component is missing required elements:', this.container);
            return;
        }

        this.currentIndex = this.tabs.findIndex(tab => tab.getAttribute('aria-selected') === 'true');
        if (this.currentIndex === -1) {
            this.currentIndex = 0; // Default to the first tab if none are active
        }

        this.resizeObserver = null;

        this.bindEvents();
        this.observeAllPanelsResize();
        this.initialHeightCheck();
    }

    /**
     * Binds all necessary event listeners.
     */
    bindEvents() {
        this.tablist.addEventListener('click', this.handleClick.bind(this));
        this.tablist.addEventListener('keydown', this.handleKeydown.bind(this));
    }

    /**
     * Handles click events on the tab list, delegating to the appropriate tab.
     * @param {MouseEvent} event - The click event.
     */
    handleClick(event) {
        const clickedTab = event.target.closest('[role="tab"]');
        if (!clickedTab) return;

        event.preventDefault();
        const newIndex = this.tabs.indexOf(clickedTab);
        this.activateTab(newIndex);
    }

    /**
     * Handles keyboard navigation within the tab list.
     * @param {KeyboardEvent} event - The keydown event.
     */
    handleKeydown(event) {
        const { key } = event;
        let newIndex = this.currentIndex;
        let flag = false;

        switch (key) {
            case 'ArrowRight':
            case 'ArrowDown':
                newIndex = (this.currentIndex + 1) % this.tabs.length;
                flag = true;
                break;
            case 'ArrowLeft':
            case 'ArrowUp':
                newIndex = (this.currentIndex - 1 + this.tabs.length) % this.tabs.length;
                flag = true;
                break;
            case 'Home':
                newIndex = 0;
                flag = true;
                break;
            case 'End':
                newIndex = this.tabs.length - 1;
                flag = true;
                break;
            default:
                break;
        }

        if (flag) {
            event.preventDefault();
            this.activateTab(newIndex, true); // Set focus to the new tab
        }
    }

    /**
     * Activates a tab and its corresponding panel.
     * @param {number} index - The index of the tab to activate.
     * @param {boolean} [setFocus=false] - Whether to set focus to the activated tab.
     */
    activateTab(index, setFocus = false) {
        if (index === this.currentIndex) return;

        // Deactivate the current tab and panel
        this.tabs[this.currentIndex].classList.remove('is-active');
        this.tabs[this.currentIndex].setAttribute('aria-selected', 'false');
        this.tabs[this.currentIndex].setAttribute('tabindex', '-1');
        this.panels[this.currentIndex].classList.remove('is-active');
        this.panels[this.currentIndex].hidden = true;

        // Activate the new tab and panel
        this.tabs[index].classList.add('is-active');
        this.tabs[index].setAttribute('aria-selected', 'true');
        this.tabs[index].setAttribute('tabindex', '0');
        this.panels[index].classList.add('is-active');
        this.panels[index].hidden = false;

        this.currentIndex = index;

        if (setFocus) {
            this.tabs[index].focus();
        }

        // The container height is now managed by the ResizeObserver on all panels,
        // so we no longer need to call height-setting functions on tab activation.
    }

    /**
     * Performs the initial height calculation. If panels contain images,
     * it waits for them to load to ensure the height is accurate. This is
     * crucial for pages loaded via AJAX/Barba.
     */
    initialHeightCheck() {
        // Find all non-lazy images within the tab panels.
        const images = Array.from(this.container.querySelectorAll('[role="tabpanel"] img:not([loading="lazy"])'));
        const imagePromises = images
            .filter(img => !img.complete) // Filter for images that haven't finished loading.
            .map(img => new Promise(resolve => {
                // Wait for either the 'load' or 'error' event.
                img.addEventListener('load', resolve, { once: true });
                img.addEventListener('error', resolve, { once: true }); // Resolve on error too, so we don't wait forever.
            }));

        // If there are no images that need loading, run the update after a brief timeout
        // to allow the browser to render after a Barba transition.
        if (imagePromises.length === 0) {
            setTimeout(() => this.updateContainerHeight(), 150);
            return;
        }

        // If there are images, wait for them all to load, then update the height.
        Promise.all(imagePromises).then(() => this.updateContainerHeight());
    }

    /**
     * Calculates the height of the tallest panel and sets the panels' container height.
     * This ensures the container is large enough for any tab's content, preventing
     * layout shifts on tab change.
     */
    updateContainerHeight() {
        requestAnimationFrame(() => {
            let maxHeight = 0;
            const containerWidth = this.panelsContainer.offsetWidth; // Get the container's width for accurate measurement.

            // To get the true height, all panels must be temporarily made measurable
            // We do this in a way that doesn't cause a visual flash or layout shift.
            this.panels.forEach(panel => {
                const wasHidden = panel.hidden;

                // Apply a 'measuring' state to all panels to override `height: 100%`
                // and ensure they are positioned to not affect layout.
                panel.hidden = false;
                panel.classList.add('is-measuring');

                // Temporarily set the width to match the container for accurate height calculation.
                panel.style.width = `${containerWidth}px`;

                // Use offsetHeight as it reliably includes vertical padding and borders.
                const panelLayoutHeight = panel.offsetHeight;

                // Also account for vertical margins on the panel itself, which offsetHeight does not include.
                const style = window.getComputedStyle(panel);
                const marginTop = parseFloat(style.marginTop) || 0;
                const marginBottom = parseFloat(style.marginBottom) || 0;
                const totalHeight = panelLayoutHeight + marginTop + marginBottom;

                maxHeight = Math.max(maxHeight, totalHeight);

                // Restore the panel's original state.
                panel.style.width = ''; // Remove inline style
                panel.classList.remove('is-measuring');
                panel.hidden = wasHidden;
            });

            // Apply the calculated max height to the container
            if (maxHeight > 0) {
                this.panelsContainer.style.minHeight = `${maxHeight}px`;
            }
        });
    }

    /**
     * Observes all panels for resize events to adjust the container height.
     */
    observeAllPanelsResize() {
        if (this.resizeObserver) {
            this.resizeObserver.disconnect();
        }

        // A single observer can watch multiple elements.
        this.resizeObserver = new ResizeObserver(() => this.updateContainerHeight());

        this.panels.forEach(panel => {
            this.resizeObserver.observe(panel);
        });
    }
}

/**
 * Initializes all tab components on the page.
 */
export function initializeTabComponents() {
    const tabComponents = document.querySelectorAll('[data-tab-component]');
    tabComponents.forEach(component => {
        if (!component.dataset.tabComponentInitialized) {
            new TabComponent(component);
            component.dataset.tabComponentInitialized = 'true';
        }
    });
}